const CARD = 'systempay'
const PAYPAL = 'paypal'
const APPLE_PAY = 'systempay_applepay'
const GOOGLE_PAY = 'systempay_googlepay'
const ONEY = 'payplug_oney'

const ONEY_COUNTRIES = ['FR', 'GP', 'MQ', 'GF', 'RE', 'YT']
const ONEY_MAXIMUM = 3000

const PAYPAL_IN_FOUR_BY_COUNTRY = {
  FR: { minimum: 20, maximum: 3000 },
}

const PAYPAL_CREDIT_BY_COUNTRY = {
  FR: { minimum: 120, maximum: 2900 },
  ES: { minimum: 120, maximum: 5000 },
  IT: { minimum: 120, maximum: 5000 },
  DE: { minimum: 99, maximum: 10000 },
  AT: { minimum: 99, maximum: 10000 },
}

export const PAYPAL_CREDIT_COUNTRIES = Object.keys(PAYPAL_CREDIT_BY_COUNTRY)

export const HOME_BANNER_INSTALMENTS_AND_CREDIT = 'instalmentsAndCredit'
export const HOME_BANNER_INSTALMENTS = 'instalments'
export const HOME_BANNER_CREDIT = 'credit'
export const HOME_BANNER_SECURE_PAYMENT = 'securePayment'

export const LEGAL_ONEY = 'oney'
export const LEGAL_PAYPAL = 'paypal'
export const LEGAL_PAYPAL_ABROAD = 'paypalAbroad'

function isAmount(value) {
  return typeof value === 'number' && !Number.isNaN(value)
}

function within(range, amount) {
  return Boolean(range) && amount >= range.minimum && amount <= range.maximum
}

export function isProCustomer(user) {
  const company = user?.company
  if (!company) return false

  return (
    Boolean(company.siret) ||
    Boolean(company.vat_is_valid && company.vat_number)
  )
}

export function paymentOffers({
  paymentMethods = [],
  country = null,
  isPro = false,
} = {}) {
  const active = new Map(
    (paymentMethods || [])
      .filter(method => method && method.is_active !== false)
      .map(method => [method.identifier, method])
  )
  const oneyMethod = active.get(ONEY)
  const oney =
    oneyMethod && ONEY_COUNTRIES.includes(country) && !isPro
      ? {
          minimum: Number(oneyMethod.minimum_total_ttc) || 0,
          maximum: ONEY_MAXIMUM,
        }
      : null
  const paypal = active.has(PAYPAL)

  return {
    country,
    oney,
    paypalInFour: paypal ? PAYPAL_IN_FOUR_BY_COUNTRY[country] || null : null,
    paypalCredit: paypal ? PAYPAL_CREDIT_BY_COUNTRY[country] || null : null,
    logos: [
      ...(active.has(CARD) ? ['cb', 'visa', 'mastercard'] : []),
      ...(paypal ? ['paypal'] : []),
      ...(active.has(APPLE_PAY) ? ['apple_pay'] : []),
      ...(active.has(GOOGLE_PAY) ? ['google_pay'] : []),
      ...(oney ? ['oney'] : []),
    ],
  }
}

const ALL_OFFERS = ['oney', 'paypalInFour', 'paypalCredit']

export function legalNotices(offers, amount, shown = ALL_OFFERS) {
  const offered = isAmount(amount)
    ? instalmentOffers(offers, amount)
    : {
        oney: Boolean(offers.oney),
        paypalInFour: Boolean(offers.paypalInFour),
        paypalCredit: Boolean(offers.paypalCredit),
      }
  const applies = name => offered[name] && shown.includes(name)
  const notices = []

  if (applies('oney')) notices.push(LEGAL_ONEY)
  if (applies('paypalInFour') || applies('paypalCredit')) {
    notices.push(offers.country === 'FR' ? LEGAL_PAYPAL : LEGAL_PAYPAL_ABROAD)
  }

  return notices
}

export function homeBanner(offers) {
  if (offers.oney && offers.paypalCredit)
    return HOME_BANNER_INSTALMENTS_AND_CREDIT
  if (offers.oney) return HOME_BANNER_INSTALMENTS
  if (offers.paypalCredit) return HOME_BANNER_CREDIT

  return HOME_BANNER_SECURE_PAYMENT
}

export function instalmentOffers(offers, amount) {
  if (!isAmount(amount)) {
    return { oney: false, paypalInFour: false, paypalCredit: false }
  }

  return {
    oney: within(offers.oney, amount),
    paypalInFour: within(offers.paypalInFour, amount),
    paypalCredit: within(offers.paypalCredit, amount),
  }
}

export const PRODUCT_INSTALMENTS_ONEY = 'oneyAndFour'
export const PRODUCT_INSTALMENTS_PAYPAL_IN_FOUR = 'paypalInFour'
export const PRODUCT_INSTALMENTS_PAYPAL_CREDIT = 'paypalCredit'

export function productInstalments(offers, amount) {
  const applies = instalmentOffers(offers, amount)

  if (applies.oney) {
    return {
      kind: PRODUCT_INSTALMENTS_ONEY,
      three: amount / 3,
      four: amount / 4,
    }
  }
  if (applies.paypalInFour) {
    return { kind: PRODUCT_INSTALMENTS_PAYPAL_IN_FOUR, four: amount / 4 }
  }
  if (applies.paypalCredit) {
    return { kind: PRODUCT_INSTALMENTS_PAYPAL_CREDIT }
  }

  return null
}

export const PLAN_ONEY_THREE = 'oneyThree'
export const PLAN_ONEY_FOUR = 'oneyFour'
export const PLAN_PAYPAL_IN_FOUR = 'paypalInFour'
export const PLAN_PAYPAL_CREDIT = 'paypalCredit'
export const PLAN_PAYPAL_CREDIT_ABROAD = 'paypalCreditAbroad'

export function instalmentPlans(offers, amount) {
  const applies = instalmentOffers(offers, amount)
  const plans = []

  if (applies.oney) {
    plans.push(
      { key: PLAN_ONEY_THREE, provider: 'oney', count: 3, each: amount / 3 },
      { key: PLAN_ONEY_FOUR, provider: 'oney', count: 4, each: amount / 4 }
    )
  }
  if (applies.paypalInFour) {
    plans.push({
      key: PLAN_PAYPAL_IN_FOUR,
      provider: 'paypal',
      count: 4,
      each: amount / 4,
    })
  }
  if (applies.paypalCredit) {
    plans.push({
      key:
        offers.country === 'FR'
          ? PLAN_PAYPAL_CREDIT
          : PLAN_PAYPAL_CREDIT_ABROAD,
      provider: 'paypal',
      count: null,
      each: null,
    })
  }

  return plans
}

export function oneyMinimumAbove(offers, amount) {
  if (!offers.oney || !isAmount(amount)) return null

  return amount < offers.oney.minimum ? offers.oney.minimum : null
}

export function paypalCreditCountry(country) {
  return PAYPAL_CREDIT_BY_COUNTRY[country] ? country : null
}

export function paypalCreditAvailable(country, amount) {
  return isAmount(amount) && within(PAYPAL_CREDIT_BY_COUNTRY[country], amount)
}

export function formatEuros(amount, locale = 'fr') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  }).format(amount)
}
